import { useTranslation } from 'react-i18next'

import { FamilyStatus, ProductClassDesignSummaryResponse } from 'core'

import { Alert, Col, ExpandableTable, Icon, Row } from 'common/components'
import { parseZoneDayMonthYearFormat } from 'common/dateFormatters'

import { differentAllowanceColumns, sameAllowanceColumns } from '../AgeAllowanceColumns'

type SubCollapseTypesProps = {
  benefitStartDate?: string
  benefitEndDate?: string
  productClassDetails: ProductClassDesignSummaryResponse
}

const SubCollapse = ({ benefitStartDate, benefitEndDate, productClassDetails }: SubCollapseTypesProps) => {
  const { t: translate } = useTranslation()

  const icons = [
    <Icon className="fal fa-user  ml-4 mr-8" />,
    <Icon className="fal fa-user-friends mr-8" />,
    <Icon className="fal fa-user-friends mr-8" />,
    <Icon className="fal fa-users mr-8" />,
  ]

  const showAgeAllowanceTable = productClassDetails.familyAgeAllowances.length > 0

  return (
    <>
      <div className="flex flex__align_center">
        <div className="color-gray-7">
          <Icon className="fal fa-calendar-star mr-4 " />
          Benefits Period:
          <span className="ml-4 semi-bold">
            {parseZoneDayMonthYearFormat(benefitStartDate)}
            {benefitEndDate && <span> - {parseZoneDayMonthYearFormat(benefitEndDate)}</span>}
          </span>
        </div>
        {productClassDetails && (
          <div className="color-gray-7 ml-16">
            <Icon className="fal fa-clock mr-8" />
            {translate('admin-dashboard.benefit-details.summary.waiting-period-label')}:
            <span className="ml-4 semi-bold">
              {productClassDetails?.waitingPeriod !== 0
                ? `${productClassDetails?.waitingPeriod} ${translate(
                    'admin-dashboard.benefit-details.summary.waiting-period-value-label',
                  )}`
                : `None`}
            </span>
          </div>
        )}
      </div>
      {productClassDetails.designCompleted && (
        <Row className="design_classes_collapse_inner mt-16">
          <Col span={24}>
            <strong className="class-summary__subtitle gray-7 mb-8">Eligible expenses</strong>
          </Col>
          <Col span={24}>
            <p className="class-summary__item gray-8 t-expense-type">
              {' '}
              {productClassDetails.reimbursementProduct.name}
            </p>
          </Col>
        </Row>
      )}

      {!showAgeAllowanceTable && productClassDetails.designCompleted && (
        <Row className="design_classes_collapse_inner mt-16">
          <Col span={24}>
            <p className="class-summary__subtitle gray-7 mb-8">
              {translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.monthly-allowance')}
            </p>
          </Col>
          <Col span={24}>
            <Row>
              <Col span={24}>
                <p className="class-summary__item gray-8 mb-8 t-offer-same-allowance">
                  {productClassDetails.offerSameAllowance
                    ? translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.same-allowance')
                    : translate('admin-dashboard.benefit-details.qsehra-benefit-design-details.different-allowance')}
                </p>
              </Col>
            </Row>

            <Row>
              <Col span={18}>
                <strong className="class-summary__item gray-8 mb-8">
                  <span>{icons[0]}&nbsp;&nbsp; Single</span>
                  <br />
                </strong>
                <strong className="class-summary__item gray-8 mb-8">
                  <span>{icons[1]}&nbsp;&nbsp; Single with dependents</span>
                  <br />
                </strong>
                <strong className="class-summary__item gray-8 mb-8">
                  <span>{icons[2]}&nbsp;&nbsp; Married</span>
                  <br />
                </strong>
                <strong className="class-summary__item gray-8 mb-8">
                  <span>{icons[3]}&nbsp;&nbsp; Married with dependents</span>
                  <br />
                </strong>
              </Col>
              <Col span={6} className="text-right">
                <strong className=" class-summary__subitem gray-7 t-single-family-allowance">
                  ${productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.Single)?.amount}
                  <br />
                </strong>
                <strong className=" class-summary__subitem gray-7 t-single-dependents-family-allowance">
                  $
                  {
                    productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.SingleWithDependents)
                      ?.amount
                  }
                  <br />
                </strong>
                <strong className=" class-summary__subitem gray-7 t-married-family-allowance">
                  ${productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.Married)?.amount}
                  <br />
                </strong>
                <strong className=" class-summary__subitem gray-7 t-married-dependents-family-allowance">
                  $
                  {
                    productClassDetails.familyAllowances.find((x) => x.familyId == FamilyStatus.MarriedWithDependents)
                      ?.amount
                  }
                  <br />
                </strong>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {showAgeAllowanceTable && (
        <>
          <Row className="design_classes_collapse_inner mt-16">
            <Col span={24}>
              <strong className="class-summary__subtitle gray-7 mb-8">ALLOWANCE TYPE</strong>
            </Col>
            <Col span={24}>
              <p className="class-summary__item gray-8 t-allowance-type">
                {productClassDetails.offerSameAllowance ? 'Age' : 'Age + Family Status'}
              </p>
            </Col>
          </Row>
          <Row className="mt-16">
            <ExpandableTable
              className={'t-product-class-details w-100'}
              dataSource={productClassDetails.familyAgeAllowances}
              loading={false}
              columns={
                productClassDetails.offerSameAllowance
                  ? sameAllowanceColumns(productClassDetails.familyAgeAllowances)
                  : differentAllowanceColumns(productClassDetails.familyAgeAllowances)
              }
              pagination={false}
            />
          </Row>
          <Alert
            type="info"
            showIcon
            description={
              'Allowances for ages in between your oldest and youngest employee age are calculated based on the difference between the oldest and youngest allowances divided by the difference between oldest and youngest ages. '
            }
          />
        </>
      )}
    </>
  )
}
export default SubCollapse
